/**
 * Topping up the wallet (MYP-55; Fiuu FPX since MYP-134) and the top-up history (MYP-107).
 *
 * The bounds and vocabulary live here, not in the screens, for the same reason `payments.ts`
 * keeps its arithmetic out of `PayFromWalletDialog`: worth testing without a browser.
 */

/**
 * Bounds, in cents. Neither the mentor's mock nor the legacy 账户充值 screenshot gave a real
 * minimum or maximum; these change once the vendor and mentor confirm the Fiuu top-up limits
 * (MYP-134). Fiuu's own FPX minimum is "more than RM 1.00".
 */
export const TOP_UP_MIN_CENTS = 1_000; // RM 10
export const TOP_UP_MAX_CENTS = 500_000; // RM 5,000

/** Whether a top-up amount (in cents) falls within the fixture bounds above. */
export function isValidTopUpAmount(cents: number): boolean {
  return (
    Number.isFinite(cents) &&
    Number.isInteger(cents) &&
    cents >= TOP_UP_MIN_CENTS &&
    cents <= TOP_UP_MAX_CENTS
  );
}

/**
 * What the customer typed in the amount box → whole cents, or undefined when it is not an amount:
 * `"100"` → 10000, `"10.5"` → 1050, `"10.05"` → 1005. Digits with at most two decimals and nothing
 * else — no sign, no comma, no currency — so `"1,000"`, `"-5"` and `"10.555"` are refused rather
 * than guessed at. Read from the digits, never through a float. Range is a separate question
 * (`isValidTopUpAmount`).
 */
export function parseTopUpAmountCents(input: string): number | undefined {
  const match = /^(\d{1,9})(?:\.(\d{1,2}))?$/.exec(input.trim());
  if (match === null) return undefined;
  const ringgit = Number(match[1]);
  const sen = Number((match[2] ?? '').padEnd(2, '0'));
  return ringgit * 100 + sen;
}

/**
 * Where one past top-up stands (MYP-107), read from `get_trade`'s `status`:
 * `success` (`END`, completed), `incomplete` (`INVALID` — started and never paid, 492 of 553 on
 * the test account), `cancelled` (`CANCEL`, the spelling the vendor says top-ups use, MYP-134),
 * `failed` and `pending` for anything else the vendor may send.
 */
export type TopUpStatus = 'success' | 'failed' | 'pending' | 'incomplete' | 'cancelled';

/**
 * How a top-up was paid, when `bankname` is one of the values seen live (MYP-134): `fiuu` for
 * "MOL PAY - ONLINE" (MOLPay is Fiuu's old name), `maybank2u` for "MAYBANK - ONLINE", `ewallet`
 * for "Ewallet Payment". Anything else has no kind, and the screen shows the vendor's own text.
 */
export type TopUpMethodKind = 'fiuu' | 'maybank2u' | 'ewallet';

/**
 * One row of top-up history (MYP-57) — the legacy 充值记录 tab equivalent, top-ups only, no
 * order-related debits (see the ticket for why that's out of scope here).
 */
export interface TopUpRecord {
  readonly id: string;
  /** The vendor's own reference for this attempt (`rechargeno`, e.g. "20260924-241640"). */
  readonly reference: string;
  /** How it was paid, as the vendor names it (`bankname`, e.g. "MAYBANK - ONLINE"). Unset when blank. */
  readonly method?: string;
  /** `method` read into a known kind, for a translated label; unset when not one seen live. */
  readonly methodKind?: TopUpMethodKind;
  /** ISO 8601, or empty when the vendor sent no usable date. */
  readonly dateIso: string;
  /** Always positive, success or failed alike — a top-up attempt only ever adds. */
  readonly amountCents: number;
  readonly currency: string;
  readonly status: TopUpStatus;
}
